import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Users, MessageSquare, Shield, Check, X, 
  Key, Trash2, RefreshCw, Search, ChevronLeft,
  BarChart3, Clock, Database
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { getInitials } from '@/lib/utils';
import { toast } from 'sonner';
import api from '@/lib/api';

export const AdminDashboard = ({ onClose }) => {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showPendingOnly, setShowPendingOnly] = useState(false);
  const [resetPasswordDialog, setResetPasswordDialog] = useState(null);
  const [newPassword, setNewPassword] = useState('');

  useEffect(() => {
    loadData();
  }, [showPendingOnly]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsRes, usersRes] = await Promise.all([
        api.get('/admin/stats'),
        api.get(`/admin/users?pending_only=${showPendingOnly}`)
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (userId) => {
    try {
      await api.post(`/admin/users/${userId}/approve`);
      toast.success('Benutzer freigeschalten');
      loadData();
    } catch (error) {
      toast.error('Fehler beim Freischalten');
    }
  };

  const handleReject = async (userId) => {
    if (!confirm('Benutzer wirklich ablehnen und löschen?')) return;
    try {
      await api.post(`/admin/users/${userId}/reject`);
      toast.success('Benutzer abgelehnt');
      loadData();
    } catch (error) {
      toast.error('Fehler beim Ablehnen');
    }
  };

  const handleDelete = async (userId) => {
    if (!confirm('Benutzer wirklich löschen?')) return;
    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success('Benutzer gelöscht');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Passwort muss mindestens 6 Zeichen haben');
      return;
    }
    try {
      await api.post('/admin/users/reset-password', {
        user_id: resetPasswordDialog.id,
        new_password: newPassword
      });
      toast.success('Passwort zurückgesetzt');
      setResetPasswordDialog(null);
      setNewPassword('');
    } catch (error) {
      toast.error('Fehler beim Zurücksetzen');
    }
  };

  const filteredUsers = users.filter(user => 
    user.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="h-full flex flex-col bg-slate-50 dark:bg-slate-950">
      {/* Header */}
      <div className="p-4 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={onClose} className="rounded-xl">
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold text-slate-900 dark:text-white">
              Admin Dashboard
            </h1>
            <p className="text-sm text-slate-500">Benutzerverwaltung & Statistiken</p>
          </div>
          <Button variant="ghost" size="icon" onClick={loadData} className="ml-auto rounded-xl">
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      <ScrollArea className="flex-1">
        <div className="p-4 space-y-6">
          {/* Stats */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <StatCard 
                icon={Users} 
                label="Benutzer" 
                value={stats.total_users}
                color="emerald"
              />
              <StatCard 
                icon={Clock} 
                label="Ausstehend" 
                value={stats.pending_users}
                color="amber"
              />
              <StatCard 
                icon={BarChart3} 
                label="Online" 
                value={stats.online_users}
                color="blue"
              />
              <StatCard 
                icon={MessageSquare} 
                label="Chats" 
                value={stats.total_chats}
                color="purple"
              />
              <StatCard 
                icon={Database} 
                label="Nachrichten" 
                value={stats.total_messages}
                color="pink"
              />
            </div>
          )}

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Benutzer suchen..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 rounded-xl"
              />
            </div>
            <Button
              variant={showPendingOnly ? 'default' : 'outline'}
              onClick={() => setShowPendingOnly(!showPendingOnly)}
              className="rounded-xl"
            >
              <Clock className="w-4 h-4 mr-2" />
              Nur ausstehende
            </Button>
          </div>

          {/* Users List */}
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm overflow-hidden">
            <div className="p-4 border-b border-slate-200 dark:border-slate-800">
              <h2 className="font-semibold text-slate-900 dark:text-white">
                Benutzer ({filteredUsers.length})
              </h2>
            </div>
            
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin h-8 w-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                Keine Benutzer gefunden
              </div>
            ) : (
              <div className="divide-y divide-slate-200 dark:divide-slate-800">
                {filteredUsers.map(user => (
                  <UserRow
                    key={user.id}
                    user={user}
                    onApprove={() => handleApprove(user.id)}
                    onReject={() => handleReject(user.id)}
                    onDelete={() => handleDelete(user.id)}
                    onResetPassword={() => setResetPasswordDialog(user)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </ScrollArea>

      {/* Reset Password Dialog */}
      <Dialog open={!!resetPasswordDialog} onOpenChange={() => setResetPasswordDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Passwort zurücksetzen</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-slate-500">
              Neues Passwort für <strong>{resetPasswordDialog?.username}</strong>
            </p>
            <div className="space-y-2">
              <Label>Neues Passwort</Label>
              <Input
                type="password"
                placeholder="Mindestens 6 Zeichen"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetPasswordDialog(null)}>
              Abbrechen
            </Button>
            <Button onClick={handleResetPassword}>
              Passwort setzen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, color }) => {
  const colors = {
    emerald: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400',
    amber: 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400',
    blue: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
    purple: 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400',
    pink: 'bg-pink-100 text-pink-600 dark:bg-pink-900/30 dark:text-pink-400',
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white dark:bg-slate-900 rounded-2xl p-4 shadow-sm"
    >
      <div className={`w-10 h-10 rounded-xl ${colors[color]} flex items-center justify-center mb-3`}>
        <Icon className="w-5 h-5" />
      </div>
      <p className="text-2xl font-bold text-slate-900 dark:text-white">{value}</p>
      <p className="text-sm text-slate-500">{label}</p>
    </motion.div>
  );
};

const UserRow = ({ user, onApprove, onReject, onDelete, onResetPassword }) => {
  const isPending = !user.is_approved;

  return (
    <div className="flex items-center gap-4 p-4 hover:bg-slate-50 dark:hover:bg-slate-800/50">
      <Avatar className="h-10 w-10">
        <AvatarImage src={user.avatar} />
        <AvatarFallback className="bg-slate-200 dark:bg-slate-700">
          {getInitials(user.username)}
        </AvatarFallback>
      </Avatar>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="font-medium text-slate-900 dark:text-white truncate">
            {user.username}
          </p>
          {user.is_superadmin && (
            <span className="px-2 py-0.5 text-xs rounded-full bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
              Admin
            </span>
          )}
          {isPending && (
            <span className="px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
              Ausstehend
            </span>
          )}
          {user.is_online && (
            <span className="w-2 h-2 rounded-full bg-emerald-500" />
          )}
        </div>
        <p className="text-sm text-slate-500 truncate">{user.email}</p>
      </div>

      <div className="flex items-center gap-2">
        {isPending ? (
          <>
            <Button
              size="icon"
              variant="ghost"
              onClick={onApprove}
              className="h-8 w-8 rounded-lg text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20"
            >
              <Check className="w-4 h-4" />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              onClick={onReject}
              className="h-8 w-8 rounded-lg text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
            >
              <X className="w-4 h-4" />
            </Button>
          </>
        ) : (
          <>
            <Button
              size="icon"
              variant="ghost"
              onClick={onResetPassword}
              className="h-8 w-8 rounded-lg"
              title="Passwort zurücksetzen"
            >
              <Key className="w-4 h-4" />
            </Button>
            {!user.is_superadmin && (
              <Button
                size="icon"
                variant="ghost"
                onClick={onDelete}
                className="h-8 w-8 rounded-lg text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                title="Benutzer löschen"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
